import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { FaHistory } from 'react-icons/fa'
import { useImmer } from 'use-immer'

import { parseParametrizatioCurveStoreData } from '@helper/grid/parametrization-curve'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'
import parametrizationCurves from '@service/parametrization-curves'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import type {
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate,
  ICurveStoreProvider,
  IDefaultDate
} from '@base/@types/providers/Parametrization/CurveStoreProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationCurveStoretHeader from '@base/variables/headers/parametrization/curve-store'

const ParametrizationCurveStoreProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Novas Lojas - Relatório')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error, setError] = useState<string | undefined>()
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [pageCurrent, setPageCurrent] = useState(1)
  const [total, setTotal] = useState(10)
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [selecteds, setSelecteds] = useImmer([] as any)

  const handleSelectAll = () => {
    if (selecteds.length < data.length) {
      const res = []
      for (const item of data) {
        res.push(item.id)
      }
      setSelecteds(res)
    } else {
      setSelecteds([])
    }
  }

  const [data, updateData] = useImmer<any[]>([])
  const [selected, setSelectedOne] = useState(0)

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit, updateCenters] = useImmer({
    centers: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    lojaIds: [],
    produtoIds: [],
    dataInicio: new Date().toISOString().split('T')[0],
    dataFim: new Date().toISOString().split('T')[0]
  })

  const handleUpdateEditParams = useCallback(
    async ({ name, value }: IUpdateParamsEdit) => {
      updateEditParams(draft => {
        draft[name] = value
      })
    },
    [updateEditParams]
  )
  const getCenters = useCallback(async () => {
    setLoadingEdit(true)
    try {
      const { data: centers } = await parametrization.getStore()

      updateCenters(draft => {
        return Object.assign(draft, {
          centers: parseCommonFilters(centers.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoadingEdit(false)
  }, [updateCenters])

  const defaultDate = useMemo(() => {
    const dateNow = new Date()
    const initialDate = dayjs(dateNow).subtract(1, 'year').format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).add(1, 'year').format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    empresaIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    periodo: new Date().toISOString(),
    produtoSemFornecedor: false,
    compradores: [],
    initialDate: defaultDate.initialDate,
    finalDate: defaultDate.finalDate
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationCurveStoretHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationCurveStoretHeader')) {
      encrypteHeaderStorange('parametrizationCurveStoretHeader', headers)
    } else {
      localStorage.removeItem('parametrizationCurveStoretHeader')
      encrypteHeaderStorange('parametrizationCurveStoretHeader', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {}, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationCurveStoretHeader')) {
      const newHeader = decrypterHeaderStorange(
        'parametrizationCurveStoretHeader'
      )
      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    if (name === 'initialDate') {
      setCurrentyDate({
        initialDate: value,
        finalDate: currentyDate.finalDate
      })
    }
    if (name === 'finalDate') {
      setCurrentyDate({
        initialDate: currentyDate.initialDate,
        finalDate: value
      })
    }

    updateParams(draft => {
      draft[name] = value
    })
  }

  const handlePageChange = useCallback(async (_page: number) => {
    setLoading(true)
  }, [])
  const getData = useCallback(
    async (search: any, page: any, total: any, isQuery: boolean) => {
      setLoading(!(page > 1))
      // await getMaintence()
      _setFetching(true)

      try {
        const paramsToSend = {
          lojaIds: paramsHelper.parse(params.lojasIds),
          produtoIds: paramsHelper.parse(params.produtosIds),
          empresaIds: paramsHelper.parse(params.empresaIds),
          dataInicio:
            currentyDate.initialDate == null
              ? defaultDate.initialDate
              : currentyDate.initialDate,
          dataFim:
            currentyDate.finalDate == null
              ? defaultDate.finalDate
              : currentyDate.finalDate,
          search: search === null ? '' : search
        }
        encrypteHeaderStorange('parametrizationCurveStoretHeader', headers)

        handleStorange()
        parametrizationCurves
          .getParamsCurveStore(search, page, 50, paramsToSend)
          .then(response => {
            const newData = [...data, ...response.data?.data]
            if (isQuery || page === 1) {
              updateData(response.data?.data)
            } else {
              updateData(newData)
            }
            setLoading(false)
            _setFetching(false)
            const pagination = response.data.pagination
            const xTotal = pagination.totalRows
            updatePagination(response.data.pagination)
            setTotal(xTotal)
            setPageCurrent(page)
          })
          .catch(() => {
            updatePagination({
              actualPage: 0,
              rows: 0,
              totalPages: 0,
              totalRows: 0,
              skip: 0
            })
            updateData([])
            setLoading(false)
            _setFetching(false)
          })
      } catch {
        setLoading(false)
      }
    },
    [
      params.lojasIds,
      params.produtosIds,
      params.empresaIds,
      currentyDate.initialDate,
      currentyDate.finalDate,
      defaultDate.initialDate,
      defaultDate.finalDate,
      headers,
      handleStorange,
      data,
      updatePagination,
      updateData
    ]
  )

  const updateDataCurve = useCallback(async () => {
    setLoading(true)

    try {
      const { data: _res } = await parametrizationCurves.postParamsCurveStore({
        lojaId: paramsEdit.lojaIds[0].value,
        produtoIds: paramsEdit.produtoIds.map(item => item.value),
        dataInicio: new Date(paramsEdit.dataInicio),
        dataFim: new Date(paramsEdit.dataFim)
      })
      toast({
        title: 'Sucesso.',
        description: 'Novas Lojas Loja editado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      setSelecteds([])
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData(null, 1, 50, false)
  }, [
    getData,
    paramsEdit.lojaIds,
    paramsEdit.produtoIds,
    paramsEdit.dataInicio,
    paramsEdit.dataFim,
    toast,
    setSelecteds
  ])

  const deleteData = useCallback(async () => {
    setLoading(true)

    try {
      await parametrizationCurves.removeParamsCurveStore({
        ids: selecteds
      })
      updateEditParams({
        lojaIds: [],
        produtoIds: [],
        dataInicio: new Date().toDateString(),
        dataFim: new Date().toDateString()
      })

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      getData(null, 1, 50, false)
      setSelecteds([])
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
      getData(null, 1, 50, false)
    }
    setSelectedOne(0)
  }, [selecteds, updateEditParams, toast, getData, setSelecteds])

  const createData = useCallback(async () => {
    setLoading(true)
    try {
      await parametrizationCurves.postParamsCurveStore({
        lojaId: paramsEdit.lojaIds[0].value,
        produtoIds: paramsEdit.produtoIds.map(item => item.value),
        dataInicio: new Date(paramsEdit.dataInicio),
        dataFim: new Date(paramsEdit.dataFim)
      })
      updateEditParams({
        lojaIds: [],
        produtoIds: [],
        dataInicio: new Date().toDateString(),
        dataFim: new Date().toDateString()
      })
      toast({
        title: 'Sucesso.',
        description: 'Novas Lojas cadastrado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      setSelecteds([])
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData(null, 1, 50, false)
  }, [
    getData,
    paramsEdit.lojaIds,
    paramsEdit.produtoIds,
    paramsEdit.dataInicio,
    paramsEdit.dataFim,
    updateEditParams,
    toast,
    setSelecteds
  ])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return [
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true
      },
      {
        id: 'empresaIds',
        label: 'Empresas',
        filterId: 'companies',
        type: 'select',
        isMulti: true
      },
      {
        id: 'produtosIds',
        label: 'Produtos',
        type: 'select-api-post',
        apiUrl: '/v1/Produto',
        isMulti: true
      },
      {
        id: 'periodo',
        label: 'Datas',
        type: 'date-input',
        isMulti: false
      }
    ]
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterCurveStore',
            data,
            headers,
            parser: parseParametrizatioCurveStoreData
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [toggleColumn, data, headers])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'lojaIds',
          value: [
            {
              label: idData[0].lojaId
                ? idData[0].lojaId + ' - ' + idData[0].lojaDescricao
                : '',
              value: idData[0].lojaId
            }
          ]
        })
        handleUpdateEditParams({
          name: 'produtoIds',
          value: [
            {
              label: idData[0].produtoId
                ? idData[0].produtoId + ' - ' + idData[0].produtoDescricao
                : '',
              value: idData[0].produtoId
            }
          ]
        })
        handleUpdateEditParams({
          name: 'dataInicio',
          value: new Date(idData[0].dataInicio).toISOString().split('T')[0]
        })
        handleUpdateEditParams({
          name: 'dataFim',
          value: new Date(idData[0].dataFim).toISOString().split('T')[0]
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const supply = index['id-id']
      let verifyUpdate = selecteds.length
      if (!selecteds.includes(supply)) {
        verifyUpdate++
        setSelecteds([...selecteds, supply])
      } else {
        verifyUpdate--
        setSelecteds(selecteds.filter((prop: any) => prop !== supply))
      }
      if (verifyUpdate === 1) {
        handleEditParams(index['id-id'])
      } else {
        updateEditParams({
          lojaIds: [],
          produtoIds: [],
          dataInicio: new Date().toDateString(),
          dataFim: new Date().toDateString()
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selecteds, setSelecteds, handleEditParams]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <FaHistory />,
        onChange: handleSelect,
        selecteds: selecteds,
        isMulti: true
      }
    ]
  }, [handleSelect, selecteds])

  const clearAllFilters = () => {
    updateParams({
      lojasIds: [],
      mundoIds: [],
      categoriasCompactasIds: [],
      fornecedoresIds: [],
      produtosIds: [],
      empresaIds: [],
      statusIds: [],
      curvaAbc: [],
      curvaIgualZero: false,
      periodo: new Date().toISOString(),
      produtoSemFornecedor: false,
      compradores: [],
      initialDate: defaultDate.initialDate,
      finalDate: defaultDate.finalDate
    })
  }

  const clearForm = () => {
    if (selecteds.length === 0) {
      updateEditParams({
        lojaIds: [],
        produtoIds: [],
        dataInicio: new Date().toDateString(),
        dataFim: new Date().toDateString()
      })
    }
  }

  return (
    <ParametrizationCurveStoreContext.Provider
      value={{
        loading,
        loadingEdit,
        data: parseParametrizatioCurveStoreData(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        query,
        filters,
        hiddenColumns,
        handlePageChange,
        pagination,
        fastQuery,
        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        getCenters,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        createData,
        handleUpdateEditParams,
        handleToogleTable,
        updateDataCurve,
        deleteData,
        setFilters,
        setFilename,
        defaultDate,
        currentyDate,
        selecteds,
        pageCurrent,
        total,
        clearForm,
        handleSelectAll,
        dataOriginal: data,
        clearAllFilters
      }}
    >
      {children}
    </ParametrizationCurveStoreContext.Provider>
  )
}

export default ParametrizationCurveStoreProvider
export const ParametrizationCurveStoreContext =
  createContext<ICurveStoreProvider>({} as ICurveStoreProvider)
